import { TemplateWatermarkProperties } from '../../template/types';
import { Config } from '../../../video-data/config';

import type { CommonAttributeSettings } from '../common';
import { DEFAULT_ATTRIBUTE_SETTINGS, defineMetadata, createAttributeSetter } from '../common';
import {
    createTemplateAttributeGetter,
    createThemeRef,
    isTemplateReference,
    ThemeRefTypes,
    validateTemplateReference,
} from './common';

type WatermarkAttributeSettings = CommonAttributeSettings & {
    supportsInherit: boolean;
};

export const WATERMARK_ATTRIBUTE_TYPE = 'watermarkAttributeType';

const DEFAULT_SETTINGS: WatermarkAttributeSettings = {
    ...DEFAULT_ATTRIBUTE_SETTINGS,
    supportsInherit: false,
};

function createWatermarkValidator(supportsInherit: boolean) {
    return function (
        this: { template?: Record<string, unknown[]>; name?: string },
        value: string,
    ): void {
        if (supportsInherit && value === Config.INHERIT_FROM_TEMPLATE) {
            return;
        }
        if (value === Config.WATERMARK_NONE) {
            return;
        }
        if (!isTemplateReference(value)) {
            throw Error(`Invalid value for watermark attribute: ${value}`);
        }
        validateTemplateReference.call(this, value, ThemeRefTypes.WATERMARK_SOURCES);
        if (!value.includes(ThemeRefTypes.WATERMARK_SOURCES)) {
            throw Error(`Invalid value for watermark theme ref: ${value}`);
        }
    };
}

export function templateWatermarkSourcesByIndex(index: number): string {
    return createThemeRef(ThemeRefTypes.WATERMARK_SOURCES, index);
}

/**
 * Creates an attribute decorator used to describe a watermark property. This will allow setting
 * either a null value or a watermark theme reference string such as themeRef_watermarks_0. This
 * requires the object to have a reference to this.template, which must contain a list of
 * watermarks (e.g. this.template.watermarks)
 *
 * If supportsInherit is enable, it means a value of "inherit from template", is supported and
 * it will look up the value on the object's template to inherit values.
 */
export function WatermarkAttribute(settings: Partial<WatermarkAttributeSettings> = {}) {
    const resolvedSettings: WatermarkAttributeSettings = { ...DEFAULT_SETTINGS, ...settings };
    const validator = createWatermarkValidator(resolvedSettings.supportsInherit);
    return function <This, Value>(
        { get, set }: ClassAccessorDecoratorTarget<This, Value>,
        context: ClassAccessorDecoratorContext,
    ): ClassAccessorDecoratorResult<This, Value> {
        defineMetadata(
            resolvedSettings,
            WATERMARK_ATTRIBUTE_TYPE,
            context,
            get as (this: unknown) => unknown,
        );

        const inheritKey = resolvedSettings.supportsInherit
            ? Config.WATERMARK_INHERIT_TEMPLATE_KEY
            : undefined;

        return {
            get: createTemplateAttributeGetter(
                get as (this: object) => unknown,
                ThemeRefTypes.WATERMARK_SOURCES,
                TemplateWatermarkProperties.SOURCE_DATA,
                inheritKey,
            ) as (this: This) => Value,
            set: createAttributeSetter(
                validator,
                get as unknown as (this: object) => string,
                set as (this: object, value: string) => void,
            ) as (this: This, value: Value) => void,
        };
    };
}
